import catalogFile from '@/data/service-catalog.json'

export type ServiceCategory = 'audit' | 'service'

export type ServiceSubcat =
  | 'crm'
  | 'analytics'
  | 'marketing'
  | 'seo'
  | 'cro'
  | 'ai'
  | 'leadgen'
  | 'ecommerce'
  | 'home-builders'

export type PricingModel = 'fixed' | 'retainer' | 'quote'

export type CatalogService = {
  id: string
  category: ServiceCategory
  subcat: ServiceSubcat
  name: string
  description: string
  logo: string
  logos?: string[]
  /** USD. Monthly when pricingModel is retainer. Omit or null for quote-only. 0 = free. */
  price?: number | null
  pricingModel: PricingModel
  /** Canonical details page. */
  href: string
  productId?: string
  deliveryTime?: string
  /** Display as "From $X" / "From $X/mo". */
  fromPrice?: boolean
  badge?: string
}

type CatalogFile = {
  _comment?: string
  services: CatalogService[]
}

const catalog = catalogFile as CatalogFile

export const SERVICE_CATALOG: CatalogService[] = catalog.services

const byId = new Map(SERVICE_CATALOG.map((item) => [item.id, item]))

export function getCatalogService(id: string): CatalogService | undefined {
  return byId.get(id)
}

export function mustGetCatalogService(id: string): CatalogService {
  const item = byId.get(id)
  if (!item) throw new Error(`Unknown service catalog id: ${id}`)
  return item
}

export function catalogByCategory(category: ServiceCategory): CatalogService[] {
  return SERVICE_CATALOG.filter((item) => item.category === category)
}

export function catalogBySubcat(subcat: ServiceSubcat): CatalogService[] {
  return SERVICE_CATALOG.filter((item) => item.subcat === subcat)
}

export function formatUsd(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(amount)
}

/** Numeric amount for Stripe / math. Throws if the SKU is quote-only. */
export function catalogAmount(id: string): number {
  const item = mustGetCatalogService(id)
  if (item.price == null) throw new Error(`Service catalog "${id}" has no numeric price`)
  return item.price
}

export function catalogDisplay(idOrItem: string | CatalogService): string {
  const item = typeof idOrItem === 'string' ? mustGetCatalogService(idOrItem) : idOrItem
  if (item.price === 0) return 'Free'
  if (item.price == null) return 'Price on request'

  const money = formatUsd(item.price)
  const labelled = item.fromPrice ? `From ${money}` : money
  if (item.pricingModel === 'retainer') return `${labelled}/mo`
  return labelled
}
